import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  CHANNEL_STATUS,
  CHANNEL_STATUS_OBJECT,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
  PHONES_FOR_INPUT,
} from '../../../../../enum/constants';
import { getIntegrationQrCode } from '../../../../../helpers/utils';
import { NotificationService } from '../../../../../services/notifications.service';
import { ChannelService } from '../../../../../services/channel.service';
import { takeUntil } from 'rxjs';
import { BaseService } from '../../../../../services/base.service';
import { WebSocketService } from '../../../../../services/gateway.service';
import { IntegrationService } from '../../../../../services/integration.service';
import { ISocketIntegrationEvent, SocketIntegrationAction } from '../integration-instagram/integration.interface';
import { BaseIntegration } from '../base-integration/base-integration';
import { phoneValidator } from '../../../../../validators/phone.validator';

@Component({
  selector: 'app-integration-whatsapp',
  templateUrl: './integration-whatsapp.component.html',
  styleUrls: ['./integration-whatsapp.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationWhatsappComponent extends BaseIntegration implements OnInit, OnDestroy {
  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    protected override readonly notificationService: NotificationService,
    protected override readonly channelService: ChannelService,
    protected override readonly integrationService: IntegrationService,
    protected readonly baseService: BaseService,
    protected readonly webSocketService: WebSocketService,
  ) {
    super(integrationService, notificationService, channelService);
  }

  @ViewChild('qrcode') qrCodeCanvas!: ElementRef;
  protected phoneControl!: FormControl;

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.channel_id = this.payload.channel_id;
    this.phoneControl = new FormControl('', [Validators.required, phoneValidator]);
    // this.phoneControl.setValue('0972045716');
    this.initSockets();
    this.getChannelById();
  }

  private initSockets(): void {
    this.webSocketService
      .whatsAppChannelAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.handleChannelAction(data);
        },
      });
  }

  protected createQrCode(): void {
    this.channelService.checkConnect({ type: CHANNEL_TYPE.WHATSAPP }).subscribe({
      next: (data: any) => {
        if (data) {
          if (this.phoneControl.invalid) {
            this.phoneControl.markAsTouched();
            return;
          }

          this.isLoader = true;
          this.integrationService
            .createWhatsApp(this.channel_id, this.phoneControl.value)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              error: (res: any) => {
                (res.error?.errors || []).forEach((err: any) => {
                  this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
                  this.isLoader = false;
                });
              },
            });
        }
      },
    });
  }

  private initQRCode(qrUrl?: string) {
    if (!this.qrCodeCanvas?.nativeElement || !qrUrl) return;
    this.qrUrl = qrUrl;

    const qrCode = getIntegrationQrCode(qrUrl, '/assets/svgs/integrations/whatsapp.svg');

    this.qrCodeCanvas.nativeElement.innerHTML = '';
    qrCode.append(this.qrCodeCanvas.nativeElement);
  }

  private handleChannelAction(data: ISocketIntegrationEvent): void {
    console.log('handleChannelAction', data);
    if (this.channel_id !== data.channel_id) return;

    switch (data.action) {
      case SocketIntegrationAction.QR_AVAILABLE: {
        this.showQr = true;
        setTimeout(() => this.initQRCode(data.data?.qrUrl), 10);
        this.isLoader = false;
        break;
      }

      case SocketIntegrationAction.PROCESSED: {
        this.isConnectionProcessed = !data.data?.afterLoginActions;
        this.isLoader = true;
        this.showQr = false;
        break;
      }

      case SocketIntegrationAction.SUCCESS: {
        this.getChannelById();
        this.showQr = false;
        this.isConnectionProcessed = false;
        break;
      }

      case SocketIntegrationAction.FAILED: {
        this.isLoader = false;
        this.showQr = false;
        this.isConnectionProcessed = false;
        if (data.error) {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[data.error] || DEFAULT_ERROR,
          );
        }
        break;
      }
    }
  }

  override channelByIdCallback(): void {
    console.log(this.phoneNumber);
    this.phoneControl.setValue(this.phoneNumber);
  }

  protected readonly CHANNEL_STATUS = CHANNEL_STATUS;
  protected readonly CHANNEL_STATUS_OBJECT = CHANNEL_STATUS_OBJECT;
  protected readonly PHONES_FOR_INPUT = PHONES_FOR_INPUT;
}
